import React from 'react';
import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIconMap } from './useIconMap';
import { Tooltip } from '../../../components';
import { ROUTES, ROUTE_CONFIG, DISCOVERY_ROUTES, DISCOVERY_ROUTE_CONFIG } from '../../../constants/routes';
import { getUserRole, isSupportUser } from '../../../lib/helpers';
import { getSessionVar } from '../../../lib/helpers';
import { useGetOrganizationQuery } from '../../../api';
import { UserRoles } from '../../../constants';

function getOpenKeyBySelectedKey(navData, selectedKey) {
    for (const item of navData) {
        if (item.children) {
            for (const child of item.children) {
                if (child.key === selectedKey) {
                    return item.key; // Return the parent key
                }
                // Handle nested children (for group structure)
                if (child.children) {
                    for (const grandchild of child.children) {
                        if (grandchild.key === selectedKey) {
                            return child.key; // return immediate parent key
                        }
                    }
                }
            }
        }
    }
    return null;
}

// Helper function to find nav item by key, including multi level nested children
function findNavItemByKey(navData, key, parentKey = null) {
    for (const nav of navData) {
        if (nav.key === key) {
            return { ...nav, parentKey };
        }

        if (nav.children) {
            const navItem = findNavItemByKey(nav.children, key, nav.key);
            if (navItem) {
                return navItem;
            }
        }
    }
    return null;
}

// Helper function to get enabled features from current organization
function getEnabledFeatures(currentOrg) {
    if (currentOrg && currentOrg.enabled_features && Array.isArray(currentOrg.enabled_features)) {
        return currentOrg.enabled_features;
    }
    // Default - just migration features if no data is available
    return null;
}

// Function to check if discovery is enabled for current organization
// will read from sessionStorage if no arg provided
function isDiscoveryEnabled(currentOrg) {
    const org = currentOrg || getSessionVar('currentOrg');
    const enabledFeatures = getEnabledFeatures(org);
    return Array.isArray(enabledFeatures) && enabledFeatures.includes('discovery');
}

// Function to check if migration is enabled for current organization
// will read from sessionStorage if no arg provided
function isMigrationEnabled(currentOrg) {
    const org = currentOrg || getSessionVar('currentOrg');
    const enabledFeatures = getEnabledFeatures(org);
    return Array.isArray(enabledFeatures) && enabledFeatures.includes('migrate');
}

export { isDiscoveryEnabled, isMigrationEnabled };

export const useNavigation = ({ collapsed }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedKey, setSelectedKey] = useState(sessionStorage.getItem('activeMenuKey') || '');
    const [openKeys, setOpenKeys] = useState([]);
    const [navItems, setNavItems] = useState([]);
    const [bottomNavItems, setBottomNavItems] = useState([]);
    const navDataRef = useRef([]);
    const iconMap = useIconMap();

    const isSupportOrSysAdmin = useMemo(() => {
        const systemRole = sessionStorage.getItem('systemRole');
        const userRole = sessionStorage.getItem('userRole');
        const rolesWithFullOrgAccess = [UserRoles.ADMIN, UserRoles.SUPPORT];

        return rolesWithFullOrgAccess.includes(systemRole) || rolesWithFullOrgAccess.includes(userRole);
    }, []);

    // Track organization ID to trigger nav rebuild when it changes
    const [currentOrgId, setCurrentOrgId] = useState(sessionStorage.getItem('userOrg'));

    // Use RTK Query to watch for organization data
    const { data: currentOrg, isSuccess: isOrgLoaded } = useGetOrganizationQuery(
        { orgId: currentOrgId },
        { skip: !currentOrgId },
    );

    // Monitor organization changes
    useEffect(() => {
        // Use currentOrg from RTK Query if available
        if (currentOrg && currentOrg.id && currentOrg.id !== currentOrgId) {
            setCurrentOrgId(currentOrg.id);
        }
    }, [location, currentOrgId, currentOrg]); // Run after org switch or location change

    // Format nav items for Ant Design Menu
    const formatNavItems = useCallback(
        (navData) => {
            return navData.map((nav) => {
                // Handle group type for section headers
                if (nav.type === 'group') {
                    return {
                        type: 'group',
                        label: nav.label,
                        key: nav.key,
                        children: nav.children
                            .filter((child) => child.visible !== false)
                            .map((child) =>
                                child.children
                                    ? {
                                          label: child.label,
                                          key: child.key,
                                          icon: collapsed ? (
                                              <Tooltip
                                                  title={child.label}
                                                  placement="right"
                                                  arrow={true}
                                                  rootClassName="sidebar-submenu-tooltip"
                                              >
                                                  {iconMap[child.key]}
                                              </Tooltip>
                                          ) : (
                                              iconMap[child.key]
                                          ),
                                          children: child.children
                                              .filter((grandchild) => grandchild.visible !== false)
                                              .map((grandchild) => ({
                                                  label: grandchild.label,
                                                  key: grandchild.key,
                                                  path: grandchild.path,
                                              })),
                                      }
                                    : {
                                          label: child.label,
                                          icon: iconMap[child.key],
                                          key: child.key,
                                          path: child.path,
                                      },
                            ),
                    };
                }

                // Handle regular menu items
                return nav.children
                    ? {
                          label: nav.label,
                          key: nav.key,
                          icon: collapsed ? (
                              <Tooltip
                                  title={nav.label}
                                  placement="right"
                                  arrow={true}
                                  rootClassName="sidebar-submenu-tooltip"
                              >
                                  {iconMap[nav.key]}
                              </Tooltip>
                          ) : (
                              iconMap[nav.key]
                          ),
                          children: nav.children
                              .filter((child) => child.visible !== false)
                              .map((child) => ({
                                  label: child.label,
                                  key: child.key,
                                  path: child.path,
                              })),
                      }
                    : {
                          label: nav.label,
                          icon: iconMap[nav.key],
                          key: nav.key,
                          path: nav.path,
                      };
            });
        },
        [iconMap, collapsed],
    );

    // Build navigation data
    useEffect(() => {
        // Don't build nav until currentOrg is loaded
        if (!isOrgLoaded || !currentOrg || !currentOrgId) {
            return;
        }

        const userRole = getUserRole();
        const isFeatureEnabled = (feature) => true; // Placeholder for feature flag

        let navData = [{ label: 'Dashboard', key: 'dashboard', path: ROUTES.home }];
        // Define discovery menu items children
        const discoveryChildren = [
            {
                label: 'Inventory',
                key: 'inventory',
                path: DISCOVERY_ROUTES.inventory,
            },
            {
                label: 'Move Groups and Apps',
                key: 'moveGroupsAndApps',
                path: '/discovery/groups',
                icon: iconMap.moveGroupsAndApps,
            },
            {
                label: 'Import',
                key: 'import',
                children: [
                    { label: DISCOVERY_ROUTE_CONFIG.rvTools.title, key: 'rvTools', path: DISCOVERY_ROUTES.rvTools },
                    {
                        label: DISCOVERY_ROUTE_CONFIG.awsTransform.title,
                        key: 'awsTransform',
                        path: DISCOVERY_ROUTES.awsTransform,
                    },
                ],
                visible: userRole !== UserRoles.GLOBAL_ADMIN,
            },
        ];

        // Define migration menu items children
        const migrationChildren = [
            {
                label: 'Project',
                key: 'project',
                children: [{ label: ROUTE_CONFIG.projects.title, key: 'projects', path: ROUTES.projects }],
                visible:
                    [UserRoles.SUPPORT, UserRoles.ADMIN, UserRoles.ORG_ADMIN, UserRoles.GLOBAL_ADMIN].includes(
                        userRole,
                    ) || isSupportUser(),
            },
            {
                label: 'Manage',
                key: 'manage',
                children: [
                    { label: ROUTE_CONFIG.clouds.title, key: 'clouds', path: ROUTES.clouds },
                    { label: ROUTE_CONFIG.sources.title, key: 'sources', path: ROUTES.sources },
                    { label: ROUTE_CONFIG.moveGroups.title, key: 'moveGroups', path: ROUTES.moveGroups },
                    ...(isFeatureEnabled('binary_assets')
                        ? [{ label: ROUTE_CONFIG.binaryAssets.title, key: 'binaryAssets', path: ROUTES.binaryAssets }]
                        : []),
                    {
                        label: ROUTE_CONFIG.migrationProfilesList.title,
                        key: 'migrationProfilesList',
                        path: ROUTES.migrationProfilesList,
                    },
                    {
                        label: ROUTE_CONFIG.migrationSettings.title,
                        key: 'migrationSettings',
                        path: ROUTES.migrationSettings,
                        reload: true,
                    },
                ],
                visible: userRole !== UserRoles.VIEWER && userRole !== UserRoles.GLOBAL_ADMIN,
            },
            {
                label: 'Migrate',
                key: 'migrate',
                children: [
                    {
                        label: ROUTE_CONFIG.startMigrations.title,
                        key: 'startMigration',
                        path: ROUTES.startMigrations,
                        visible: userRole !== UserRoles.VIEWER,
                        reload: true,
                    },
                    { label: ROUTE_CONFIG.migrationList.title, key: 'migrationList', path: ROUTES.migrationList },
                    {
                        label: ROUTE_CONFIG.migrationPlanner.title,
                        key: 'migrationPlanner',
                        path: ROUTES.migrationPlanner,
                        visible: userRole !== UserRoles.VIEWER,
                    },
                ],
                visible: userRole !== UserRoles.GLOBAL_ADMIN,
            },
            {
                label: 'Backup',
                key: 'backup',
                children: [
                    {
                        label: ROUTE_CONFIG.backupApplianceSetup.title,
                        key: 'backupApplianceSetup',
                        path: ROUTES.backupApplianceSetup,
                    },
                    {
                        label: ROUTE_CONFIG.backupPolicies.title,
                        key: 'recoveryDrPolicies',
                        path: ROUTES.backupPolicies,
                    },
                ],
                visible: userRole !== UserRoles.VIEWER && userRole !== UserRoles.GLOBAL_ADMIN,
            },
            {
                label: 'Recovery',
                key: 'recovery',
                children: [
                    {
                        label: ROUTE_CONFIG.recoveryApplianceSetup.title,
                        key: 'recoveryApplianceSetup',
                        path: ROUTES.recoveryApplianceSetup,
                    },
                    {
                        label: ROUTE_CONFIG.recoveryDrPolicies.title,
                        key: 'drPolicies',
                        path: ROUTES.recoveryDrPolicies,
                    },
                ],
                visible: userRole !== UserRoles.VIEWER && userRole !== UserRoles.GLOBAL_ADMIN,
            },
            {
                label: 'Reporting',
                key: 'reporting',
                children: [
                    {
                        label: ROUTE_CONFIG.reportingMigrationProjectPlan.title,
                        key: 'reportingMigrationProjectPlan',
                        path: ROUTES.reportingMigrationProjectPlan,
                    },
                    {
                        label: ROUTE_CONFIG.reportingMigrationStatus.title,
                        key: 'reportingMigrationStatus',
                        path: ROUTES.reportingMigrationStatus + '?type=full',
                        reload: true,
                    },
                    {
                        label: ROUTE_CONFIG.reportingMigrationStatusDiff.title,
                        key: 'reportingMigrationStatusDiff',
                        path: ROUTES.reportingMigrationStatusDiff + '?type=differential',
                        reload: true,
                    },
                    {
                        label: ROUTE_CONFIG.reportingMigrationByUser.title,
                        key: 'reportingMigrationByUser',
                        path: ROUTES.reportingMigrationByUser,
                    },
                    {
                        label: ROUTE_CONFIG.reportingEntitlementStatus.title,
                        key: 'reportingEntitlementStatus',
                        path: ROUTES.reportingEntitlementStatus,
                    },
                    {
                        label: ROUTE_CONFIG.reportingEntitlementsConsumption.title,
                        key: 'reportingEntitlementsConsumption',
                        path: ROUTES.reportingEntitlementsConsumption,
                    },
                    {
                        label: ROUTE_CONFIG.reportingBackupPolicies.title,
                        key: 'reportingBackupPolicies',
                        path: ROUTES.reportingBackupPolicies,
                    },
                    {
                        label: ROUTE_CONFIG.reportingBackups.title,
                        key: 'reportingBackups',
                        path: ROUTES.reportingBackups,
                    },
                    {
                        label: ROUTE_CONFIG.reportingDrPolicies.title,
                        key: 'reportingDrPolicies',
                        path: ROUTES.reportingDrPolicies,
                    },
                    {
                        label: ROUTE_CONFIG.reportingDrReplications.title,
                        key: 'reportingDrReplications',
                        path: ROUTES.reportingDrReplications,
                    },
                    { label: ROUTE_CONFIG.auditLog.title, key: 'auditLog', path: ROUTES.auditLog },
                    {
                        label: ROUTE_CONFIG.reportingMigrationsAwsTargetInstance.title,
                        key: 'reportingMigrationsAwsTargetInstance',
                        path: ROUTES.reportingMigrationsAwsTargetInstance,
                    },
                ],
                visible: userRole === UserRoles.ADMIN || userRole === UserRoles.ORG_ADMIN || isSupportOrSysAdmin,
            },
            {
                label: ROUTE_CONFIG.admin.title,
                key: 'admin',
                path: ROUTES.admin,
                reload: true,
                visible: userRole === UserRoles.ADMIN,
            },
        ];

        const bottomMenuItems = [
            {
                label: 'Support',
                key: 'support',
                redirection: true,
                path: 'https://rivermeadow.zendesk.com/hc/en-us/requests/new',
            },
            {
                label: 'Documentation',
                key: 'documentation',
                redirection: true,
                path: 'https://docs.rivermeadow.com/home',
            },
        ];

        // Show headers if both discovery and migration are enabled
        if (isDiscoveryEnabled(currentOrg) && isMigrationEnabled(currentOrg)) {
            navData.push({
                type: 'group',
                label: 'Discovery',
                key: 'discovery-header',
                children: discoveryChildren,
            });
            navData.push({
                type: 'group',
                label: 'Migration',
                key: 'migration-header',
                children: migrationChildren,
            });
        }
        // Only show migration menu if only migration is enabled
        else if (isMigrationEnabled(currentOrg) && !isDiscoveryEnabled(currentOrg)) {
            navData.push(...migrationChildren);
        }
        // Only show discovery menu if only discovery is enabled
        else if (!isMigrationEnabled(currentOrg) && isDiscoveryEnabled(currentOrg)) {
            navData.push(...discoveryChildren);
        }
        // If neither feature is enabled, show migration menu
        // Most orgs do not have any enabled features so this is the default for now
        // Can be removed in the future when all orgs have enabled features set
        else {
            navData.push(...migrationChildren);
        }

        // Filter out nav items that are not visible and those with no visible children
        const filteredNavData = navData.filter((nav) => {
            if (nav.visible === false) return false;
            // If nav has children, filter out invisible children and check if any remain
            if (nav.children) {
                const visibleChildren = nav.children.filter((child) => child.visible !== false);
                if (visibleChildren.length === 0) return false;
                // Update nav with only visible children
                nav.children = visibleChildren;
            }
            return true;
        });

        // Set main nav items and bottom nav items separately
        const navDataWithBottom = [...filteredNavData, ...bottomMenuItems];
        setNavItems(formatNavItems(navDataWithBottom));
        navDataRef.current = navDataWithBottom;
    }, [getUserRole, formatNavItems, location, currentOrgId, isOrgLoaded, currentOrg]);

    // Helper function to find nav item by path
    const findNavItemByPath = useCallback(
        (path) => {
            const allItems = [];

            function flatten(items) {
                items.forEach((item) => {
                    if (item.path) allItems.push(item);
                    if (item.children) flatten(item.children);
                });
            }
            flatten(navItems);
            return allItems.filter((item) => item.path === path);
        },
        [navItems],
    );

    // Helper function to update selected key and open keys
    const updateSelectedState = useCallback((key) => {
        if (!key) return;

        setSelectedKey(key);
        sessionStorage.setItem('activeMenuKey', key);
    }, []);

    // Helper function to validate if a key exists in navItems
    const isValidNavKey = useCallback(
        (key) => {
            return navItems.some(
                (item) => item.key === key || (item.children && item.children.some((child) => child.key === key)),
            );
        },
        [navItems],
    );

    // Update selectedKey based on current location and stored preference
    useEffect(() => {
        if (!navItems.length) return;
        const currentPath = location.pathname;
        const storedKey = sessionStorage.getItem('activeMenuKey');

        // Priority 1: Use stored key if it's valid and matches current path
        if (storedKey && isValidNavKey(storedKey)) {
            const storedItem = findNavItemByKey(navDataRef.current, storedKey);
            if (storedItem && storedItem.path === currentPath) {
                updateSelectedState(storedKey);
                return;
            }
        }

        // Priority 2: Find items matching current path
        const matchingItems = findNavItemByPath(currentPath);

        if (matchingItems.length === 1) {
            // Single match - use it
            updateSelectedState(matchingItems[0].key);
        } else if (matchingItems.length > 1) {
            // Multiple matches for same path - prefer stored key if it's one of them
            const storedMatch = matchingItems.find((item) => item.key === storedKey);
            if (storedMatch) {
                updateSelectedState(storedKey);
            } else {
                // No stored preference, use first match (could be improved with better priority logic)
                updateSelectedState(matchingItems[0].key);
            }
        } else if (storedKey && isValidNavKey(storedKey)) {
            // Priority 3: Fallback to stored key even if path doesn't match
            updateSelectedState(storedKey);
        }
    }, [navItems, location.pathname, collapsed, findNavItemByPath, updateSelectedState, isValidNavKey]);

    // Set openKeys based on selectedKey whenever navItems or selectedKey changes
    useEffect(() => {
        if (!selectedKey || !navItems.length || collapsed) {
            return;
        }

        const openKey = getOpenKeyBySelectedKey(navDataRef.current, selectedKey);
        if (openKey) {
            setOpenKeys([openKey]);
        } else {
            // If no parent key found (top-level item), clear all open keys
            setOpenKeys([]);
        }
    }, [selectedKey, navItems, collapsed]);

    // Handle menu click
    const onClick = useCallback(
        (item) => {
            setSelectedKey(item.key);
            sessionStorage.setItem('activeMenuKey', item.key);

            const navItem = findNavItemByKey(navDataRef.current, item.key);
            const path = navItem?.path || ROUTES[item.key] || DISCOVERY_ROUTES[item.key] || '/';
            const reload = !!navItem?.reload;
            const redirection = !!navItem?.redirection;

            if (redirection) {
                window.open(path);
            } else {
                window.reactNavigate(path, reload);
            }
        },
        [navigate],
    );

    const findOpenKeyBySelectedKey = useMemo(() => {
        return getOpenKeyBySelectedKey(navItems, selectedKey);
    }, [navItems, selectedKey]);

    const handleOpenChange = useCallback((keys) => {
        setOpenKeys(keys);
    }, []);

    const navigateToHome = useCallback(() => {
        setSelectedKey('');
        sessionStorage.setItem('activeMenuKey', '');
        navigate(ROUTES.home);
    }, [navigate]);

    return {
        selectedKey,
        openKeys,
        navItems,
        bottomNavItems,
        onClick,
        setOpenKeys,
        findOpenKeyBySelectedKey,
        handleOpenChange,
        navigateToHome,
    };
};
